/**
 * Portfolio calculations for the Advanced Retirement Calculator
 * @module domain/retirement/portfolio
 */

import { AssetCategory, SellType } from "./constants";
import type { 
  PortfolioByCategory, 
  CategoryBalances, 
  ProductBreakdown,
  ProductBalance,
  ReturnRiskAssumption,
  AdvancedRetirementInputs,
  BuyingCreditResult
} from "./types";
import { 
  calculateDetailedLifeEventsImpact,
  processSellRequests,
  processBuyFromCredit,
  processSellRequestsFromProducts,
  processBuyFromCreditToProducts,
} from "./lifeEvents";
import { PENSION_TAX } from "./pensionLaw";
import { findProfileById, deriveSigmaFromProfile } from "./riskProfiles";
import type { RiskProfile } from "./riskProfiles";

// ============================================================================
// PORTFOLIO CALCULATIONS
// ============================================================================

/**
 * Recalculate portfolio by category from product breakdowns
 * Only includes products where includedInPortfolio is true
 */
export function recalculatePortfolioFromProducts(
  productBreakdowns: ProductBreakdown[]
): PortfolioByCategory {
  const portfolio: PortfolioByCategory = {
    [AssetCategory.CASH]: 0,
    [AssetCategory.DEPOSIT]: 0,
    [AssetCategory.STOCKS]: 0,
    [AssetCategory.CRYPTO]: 0,
    [AssetCategory.RSU]: 0,
    [AssetCategory.PENSION]: 0,
    [AssetCategory.REALESTATE]: 0,
    [AssetCategory.OTHER]: 0,
    [AssetCategory.DEBT]: 0,
  };

  for (const product of productBreakdowns) {
    if (product.includedInPortfolio && product.category !== AssetCategory.DEBT) {
      portfolio[product.category] += product.valueInBaseCurrency;
    }
  }

  return portfolio;
}

/**
 * Calculate total starting portfolio based on settings
 */
export function calculateStartingPortfolio(
  portfolioByCategory: PortfolioByCategory,
  includeNonLiquidRealestate: boolean,
  includeNonLiquidPension: boolean,
  productBreakdowns?: ProductBreakdown[]
): number {
  let total = 0;

  // Always include liquid assets
  total += portfolioByCategory[AssetCategory.CASH];
  total += portfolioByCategory[AssetCategory.DEPOSIT];
  total += portfolioByCategory[AssetCategory.STOCKS];
  total += portfolioByCategory[AssetCategory.CRYPTO];
  total += portfolioByCategory[AssetCategory.RSU];
  total += portfolioByCategory[AssetCategory.OTHER];

  // Handle real estate based on setting
  if (includeNonLiquidRealestate) {
    total += portfolioByCategory[AssetCategory.REALESTATE];
  } else if (productBreakdowns) {
    // Only include liquid real estate
    const liquidRealestate = productBreakdowns
      .filter(p => p.category === AssetCategory.REALESTATE && p.isLiquid && p.includedInPortfolio)
      .reduce((sum, p) => sum + p.valueInBaseCurrency, 0);
    total += liquidRealestate;
  }

  // Handle pension based on setting
  if (includeNonLiquidPension) {
    total += portfolioByCategory[AssetCategory.PENSION];
  } else if (productBreakdowns) {
    // Only include liquid pension
    const liquidPension = productBreakdowns
      .filter(p => p.category === AssetCategory.PENSION && p.isLiquid && p.includedInPortfolio)
      .reduce((sum, p) => sum + p.valueInBaseCurrency, 0);
    total += liquidPension;
  }

  return total;
}

/**
 * Calculate weighted return and standard deviation for portfolio
 */
/**
 * Resolve the effective standard deviation for a return assumption.
 * Prefers risk-profile-derived sigma when riskProfileId is set.
 */
function resolveStdDev(
  assumption: ReturnRiskAssumption,
  customProfiles?: RiskProfile[]
): number {
  if (assumption.riskProfileId) {
    const profile = findProfileById(assumption.riskProfileId, customProfiles);
    if (profile) return deriveSigmaFromProfile(profile);
  }
  return assumption.standardDeviation;
}

export function calculateWeightedReturns(
  portfolioByCategory: PortfolioByCategory,
  returnAssumptions: Record<AssetCategory, ReturnRiskAssumption>,
  customProfiles?: RiskProfile[]
): { weightedReturn: number; weightedStdDev: number } {
  const total = Object.values(portfolioByCategory).reduce((sum, val) => sum + val, 0);
  
  if (total === 0) {
    return { weightedReturn: 0.04, weightedStdDev: 0.12 }; // Defaults (balanced fund assumption)
  }

  let weightedReturn = 0;
  let weightedVariance = 0;

  for (const [category, value] of Object.entries(portfolioByCategory)) {
    const weight = value / total;
    const assumptions = returnAssumptions[category as AssetCategory];
    
    if (weight > 0 && assumptions) {
      weightedReturn += weight * assumptions.expectedReturn;
      const stdDev = resolveStdDev(assumptions, customProfiles);
      // Simplified: assume uncorrelated assets for variance
      weightedVariance += weight * weight * stdDev * stdDev;
    }
  }

  return { 
    weightedReturn, 
    weightedStdDev: Math.sqrt(weightedVariance) 
  };
}

// ============================================================================
// PRODUCT BALANCE UTILITIES
// ============================================================================

/**
 * Initialize product balances from product breakdowns
 * Converts ProductBreakdown[] to ProductBalance[] for tracking through projection
 */
export function initializeProductBalances(
  productBreakdowns: ProductBreakdown[],
  includeNonLiquidRealestate: boolean,
  includeNonLiquidPension: boolean
): { spendableProducts: ProductBalance[]; lockedPensionProducts: ProductBalance[] } {
  const spendableProducts: ProductBalance[] = [];
  const lockedPensionProducts: ProductBalance[] = [];

  for (const product of productBreakdowns) {
    if (!product.includedInPortfolio) continue;
    // Skip DEBT products — they are display-only liabilities, not investable
    if (product.category === AssetCategory.DEBT) continue;

    const productBalance: ProductBalance = {
      productId: product.productId,
      productName: product.productName,
      category: product.category,
      balance: product.valueInBaseCurrency,
      isLiquid: product.isLiquid,
      customReturnAssumption: product.customReturnAssumption,
    };

    // Handle pension products - locked until retirement
    if (product.category === AssetCategory.PENSION) {
      if (includeNonLiquidPension) {
        spendableProducts.push(productBalance);
      } else {
        lockedPensionProducts.push(productBalance);
      }
    }
    // Handle real estate
    else if (product.category === AssetCategory.REALESTATE) {
      if (includeNonLiquidRealestate || product.isLiquid) {
        spendableProducts.push(productBalance);
      }
      // Skip non-liquid real estate if not included
    }
    // All other categories are spendable
    else {
      spendableProducts.push(productBalance);
    }
  }

  return { spendableProducts, lockedPensionProducts };
}

/**
 * Get total value of product balances
 */
export function totalProductBalances(products: ProductBalance[]): number {
  return products.reduce((sum, p) => sum + p.balance, 0);
}

/**
 * Convert product balances to category balances (for compatibility)
 */
export function productBalancesToCategoryBalances(products: ProductBalance[]): CategoryBalances {
  const balances: CategoryBalances = {
    [AssetCategory.CASH]: 0,
    [AssetCategory.DEPOSIT]: 0,
    [AssetCategory.STOCKS]: 0,
    [AssetCategory.CRYPTO]: 0,
    [AssetCategory.RSU]: 0,
    [AssetCategory.PENSION]: 0,
    [AssetCategory.REALESTATE]: 0,
    [AssetCategory.OTHER]: 0,
    [AssetCategory.DEBT]: 0,
  };

  for (const product of products) {
    balances[product.category] += product.balance;
  }

  return balances;
}

/**
 * Apply returns to each product based on its custom or category return assumption
 */
export function applyReturnsPerProduct(
  products: ProductBalance[],
  categoryAssumptions: Record<AssetCategory, ReturnRiskAssumption>
): number {
  let totalReturn = 0;

  for (const product of products) {
    if (product.balance > 0) {
      // Use custom return if specified, otherwise fall back to category default
      const returnRate = product.customReturnAssumption?.expectedReturn 
        ?? categoryAssumptions[product.category]?.expectedReturn 
        ?? 0;
      const returnAmount = product.balance * returnRate;
      product.balance += returnAmount;
      totalReturn += returnAmount;
    }
  }

  return totalReturn;
}

/**
 * Calculate weighted return and standard deviation from product balances
 * Accounts for per-product custom yields
 */
export function calculateWeightedReturnsFromProducts(
  products: ProductBalance[],
  categoryAssumptions: Record<AssetCategory, ReturnRiskAssumption>,
  customProfiles?: RiskProfile[]
): { weightedReturn: number; weightedStdDev: number } {
  const total = totalProductBalances(products);
  
  if (total === 0) {
    return { weightedReturn: 0.04, weightedStdDev: 0.12 }; // Defaults (balanced fund assumption)
  }

  let weightedReturn = 0;
  let weightedVariance = 0;

  for (const product of products) {
    const weight = product.balance / total;
    if (weight > 0) {
      // Use custom return/stddev if specified, otherwise fall back to category
      const expectedReturn = product.customReturnAssumption?.expectedReturn 
        ?? categoryAssumptions[product.category]?.expectedReturn 
        ?? 0;
      
      // Resolve stdDev: custom riskProfileId > custom standardDeviation snapshot > category
      let stdDev: number;
      if (product.customReturnAssumption?.riskProfileId) {
        // Prefer live derivation from the custom risk profile (stays up-to-date if profile is edited)
        const customProfile = findProfileById(product.customReturnAssumption.riskProfileId, customProfiles);
        stdDev = customProfile
          ? deriveSigmaFromProfile(customProfile)
          : (product.customReturnAssumption.standardDeviation ?? 0);
      } else if (product.customReturnAssumption?.standardDeviation !== undefined) {
        // Legacy: use snapshot standardDeviation
        stdDev = product.customReturnAssumption.standardDeviation;
      } else {
        const catAssumption = categoryAssumptions[product.category];
        stdDev = catAssumption ? resolveStdDev(catAssumption, customProfiles) : 0;
      }
      
      weightedReturn += weight * expectedReturn;
      // Simplified: assume uncorrelated assets for variance
      weightedVariance += weight * weight * stdDev * stdDev;
    }
  }

  return { 
    weightedReturn, 
    weightedStdDev: Math.sqrt(weightedVariance) 
  };
}

/**
 * Withdraw from products proportionally
 * Returns the amount actually withdrawn
 */
export function withdrawFromProductsProportionally(
  amount: number,
  products: ProductBalance[],
  preferCashFirst: boolean = true
): number {
  let remaining = amount;
  
  // Prefer cash and deposit products first if available
  if (preferCashFirst) {
    const cashProducts = products.filter(p => (p.category === AssetCategory.CASH || p.category === AssetCategory.DEPOSIT) && p.balance > 0);
    for (const product of cashProducts) {
      const fromProduct = Math.min(remaining, product.balance);
      product.balance -= fromProduct;
      remaining -= fromProduct;
      if (remaining <= 0) return amount;
    }
  }

  // Withdraw proportionally from remaining products
  const total = totalProductBalances(products);
  if (total <= 0) {
    // No positive balance left — shortfall becomes debt (synthetic product with negative balance)
    const debtProduct = products.find((p) => p.category === AssetCategory.CASH && p.balance < 0);
    if (debtProduct) {
      debtProduct.balance -= remaining;
    } else {
      products.push(createSyntheticProduct("חוב", AssetCategory.CASH, -remaining));
    }
    return amount;
  }

  const withdrawRatio = Math.min(1, remaining / total);
  for (const product of products) {
    if (product.balance > 0) {
      const toWithdraw = product.balance * withdrawRatio;
      product.balance -= toWithdraw;
      remaining -= toWithdraw;
    }
  }

  // Any remaining shortfall becomes debt
  if (remaining > 0) {
    const debtProduct = products.find((p) => p.category === AssetCategory.CASH && p.balance < 0);
    if (debtProduct) {
      debtProduct.balance -= remaining;
    } else {
      products.push(createSyntheticProduct("חוב", AssetCategory.CASH, -remaining));
    }
  }

  return amount;
}

/**
 * Create a synthetic product for life event purchases.
 * If a stableId is provided, it is used as the productId (allowing the product to be targeted for selling).
 */
export function createSyntheticProduct(
  name: string,
  category: AssetCategory,
  amount: number,
  customReturnAssumption?: Partial<ReturnRiskAssumption>,
  stableId?: string
): ProductBalance {
  return {
    productId: stableId || `synthetic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    productName: name,
    category,
    balance: amount,
    isLiquid: true,
    customReturnAssumption,
  };
}

/**
 * Add a purchase to product balances
 * If the purchase has a stableId, look for an existing product with that id first.
 * If a matching product exists (same category and custom yield), add to it.
 * Otherwise create a new synthetic product.
 */
export function addPurchaseToProducts(
  products: ProductBalance[],
  category: AssetCategory,
  amount: number,
  name?: string,
  customReturnAssumption?: Partial<ReturnRiskAssumption>,
  stableId?: string
): void {
  // If there's a stableId, try to find existing product with that id and add to it
  if (stableId) {
    const existingById = products.find(p => String(p.productId) === stableId);
    if (existingById) {
      existingById.balance += amount;
      return;
    }
  }

  // If there's a custom return assumption, always create a new product to track it separately
  if (customReturnAssumption && (customReturnAssumption.expectedReturn !== undefined || customReturnAssumption.standardDeviation !== undefined || customReturnAssumption.riskProfileId !== undefined)) {
    products.push(createSyntheticProduct(
      name || `רכישה - ${category}`,
      category,
      amount,
      customReturnAssumption,
      stableId
    ));
    return;
  }

  // Otherwise, try to find an existing product in the same category without custom yield
  const existingProduct = products.find(
    p => p.category === category && !p.customReturnAssumption
  );

  if (existingProduct) {
    existingProduct.balance += amount;
  } else {
    // Create a new synthetic product without custom yield
    products.push(createSyntheticProduct(
      name || `רכישה - ${category}`,
      category,
      amount,
      undefined,
      stableId
    ));
  }
}

// ============================================================================
// CATEGORY BALANCE UTILITIES
// ============================================================================

/**
 * Initialize category balances from portfolio
 */
export function initializeCategoryBalances(
  portfolioByCategory: PortfolioByCategory,
  includeNonLiquidRealestate: boolean,
  includeNonLiquidPension: boolean
): { spendable: CategoryBalances; lockedPension: number } {
  // Initialize spendable balances (pension is locked by default before retirement)
  const spendable: CategoryBalances = {
    [AssetCategory.CASH]: portfolioByCategory[AssetCategory.CASH],
    [AssetCategory.DEPOSIT]: portfolioByCategory[AssetCategory.DEPOSIT],
    [AssetCategory.STOCKS]: portfolioByCategory[AssetCategory.STOCKS],
    [AssetCategory.CRYPTO]: portfolioByCategory[AssetCategory.CRYPTO],
    [AssetCategory.RSU]: portfolioByCategory[AssetCategory.RSU],
    [AssetCategory.PENSION]: 0, // Pension starts locked
    [AssetCategory.REALESTATE]: includeNonLiquidRealestate ? portfolioByCategory[AssetCategory.REALESTATE] : 0,
    [AssetCategory.OTHER]: portfolioByCategory[AssetCategory.OTHER],
    [AssetCategory.DEBT]: 0, // DEBT is display-only, not spendable
  };

  // Pension is locked until retirement by default
  const lockedPension = includeNonLiquidPension ? 0 : portfolioByCategory[AssetCategory.PENSION];

  return { spendable, lockedPension };
}

/**
 * Get total of category balances
 */
export function totalCategoryBalances(balances: CategoryBalances): number {
  return Object.values(balances).reduce((sum, val) => sum + val, 0);
}

/**
 * Calculate target allocation excluding locked pension and zero categories
 */
export function calculateTargetAllocation(
  portfolioByCategory: PortfolioByCategory,
  excludePension: boolean
): Partial<Record<AssetCategory, number>> {
  const allocation: Partial<Record<AssetCategory, number>> = {};
  let total = 0;

  for (const [category, value] of Object.entries(portfolioByCategory)) {
    if (excludePension && category === AssetCategory.PENSION) continue;
    if (value > 0) {
      total += value;
    }
  }

  if (total === 0) {
    // Default to stocks if nothing else
    return { [AssetCategory.STOCKS]: 1 };
  }

  for (const [category, value] of Object.entries(portfolioByCategory)) {
    if (excludePension && category === AssetCategory.PENSION) continue;
    if (value > 0) {
      allocation[category as AssetCategory] = value / total;
    }
  }

  return allocation;
}

/**
 * Allocate contributions across categories based on target allocation
 */
export function allocateContributions(
  amount: number,
  targetAllocation: Partial<Record<AssetCategory, number>>,
  balances: CategoryBalances
): void {
  for (const [category, weight] of Object.entries(targetAllocation)) {
    const contribution = amount * (weight || 0);
    balances[category as AssetCategory] += contribution;
  }
}

/**
 * Withdraw from categories proportionally
 */
export function withdrawProportionally(
  amount: number,
  balances: CategoryBalances,
  preferCashFirst: boolean = true
): number {
  let remaining = amount;
  
  // Prefer cash and deposits first if available
  if (preferCashFirst) {
    if (balances[AssetCategory.CASH] > 0) {
      const fromCash = Math.min(remaining, balances[AssetCategory.CASH]);
      balances[AssetCategory.CASH] -= fromCash;
      remaining -= fromCash;
    }
    if (remaining > 0 && balances[AssetCategory.DEPOSIT] > 0) {
      const fromDeposit = Math.min(remaining, balances[AssetCategory.DEPOSIT]);
      balances[AssetCategory.DEPOSIT] -= fromDeposit;
      remaining -= fromDeposit;
    }
  }

  if (remaining <= 0) return amount;

  // Withdraw proportionally from other categories
  const total = totalCategoryBalances(balances);
  if (total <= 0) {
    // No positive balance left — shortfall becomes debt (negative CASH)
    balances[AssetCategory.CASH] = (balances[AssetCategory.CASH] ?? 0) - remaining;
    return amount;
  }

  const withdrawRatio = Math.min(1, remaining / total);
  for (const category of Object.values(AssetCategory)) {
    if (balances[category] > 0) {
      const toWithdraw = balances[category] * withdrawRatio;
      balances[category] -= toWithdraw;
      remaining -= toWithdraw;
    }
  }

  // Any remaining shortfall becomes debt (negative CASH)
  if (remaining > 0) {
    balances[AssetCategory.CASH] = (balances[AssetCategory.CASH] ?? 0) - remaining;
  }

  return amount;
}

/**
 * Apply returns to each category based on its return assumption
 */
export function applyReturnsPerCategory(
  balances: CategoryBalances,
  returnAssumptions: Record<AssetCategory, ReturnRiskAssumption>
): number {
  let totalReturn = 0;

  for (const category of Object.values(AssetCategory)) {
    if (balances[category] > 0) {
      const returnRate = returnAssumptions[category]?.expectedReturn || 0;
      const returnAmount = balances[category] * returnRate;
      balances[category] += returnAmount;
      totalReturn += returnAmount;
    }
  }

  return totalReturn;
}

// ============================================================================
// BUYING CREDIT CALCULATIONS
// ============================================================================

/**
 * Calculate available buying credit for a specific year.
 * Buying credit = CASH balance in the portfolio.
 * Simulates from current year to target year, tracking credit accumulation.
 * 
 * When `productBreakdowns` is provided, also tracks product-level balances
 * through the simulation so each product's projected value at the target year
 * is available (useful for the sell asset picker to show projected values).
 */
export function calculateBuyingCreditForYear(
  inputs: AdvancedRetirementInputs,
  targetYear: number,
  productBreakdowns?: ProductBreakdown[]
): BuyingCreditResult {
  const { 
    currentAge, 
    retirementAge,
    monthlySavings, 
    lifeEvents,
    startPortfolioByCategory,
    includeNonLiquidRealestate,
    includeNonLiquidPension,
    returnAssumptions,
  } = inputs;

  // Initialize category balances
  const { spendable: categoryBalances, lockedPension: initialLockedPension } =
    initializeCategoryBalances(startPortfolioByCategory, includeNonLiquidRealestate, includeNonLiquidPension);

  // Track locked pension balance for sell events
  let lockedPensionBalance = initialLockedPension;

  // Initialize product-level tracking if breakdowns are provided
  const useProducts = !!productBreakdowns && productBreakdowns.length > 0;
  let products: ProductBalance[] = [];
  if (useProducts) {
    const { spendableProducts } = initializeProductBalances(
      productBreakdowns!, includeNonLiquidRealestate, includeNonLiquidPension
    );
    products = spendableProducts;
  }

  let totalFromSavings = 0;
  let totalFromSales = 0;
  let totalFromInflows = 0;
  let totalUsedForPurchases = 0;

  const currentYear = new Date().getFullYear();
  const pensionEligibilityAge = inputs.pensionEligibilityAge ?? retirementAge;

  // Simulate up to target year
  for (let year = currentYear; year <= targetYear; year++) {
    const age = currentAge + (year - currentYear);
    const isRetired = age >= retirementAge;

    // Unlock pension at eligibility age
    if (age === pensionEligibilityAge && lockedPensionBalance > 0) {
      categoryBalances[AssetCategory.PENSION] = (categoryBalances[AssetCategory.PENSION] || 0) + lockedPensionBalance;
      lockedPensionBalance = 0;
    }

    const eventsResult = calculateDetailedLifeEventsImpact(lifeEvents, year);

    // Process sells — pension sells pull from locked pool with tax
    if (eventsResult.sellRequests.length > 0) {
      const pensionSells = eventsResult.sellRequests.filter(r => r.category === AssetCategory.PENSION);
      const otherSells = eventsResult.sellRequests.filter(r => r.category !== AssetCategory.PENSION);

      // Handle pension early withdrawal from locked pool (before eligibility)
      if (pensionSells.length > 0 && lockedPensionBalance > 0) {
        for (const req of pensionSells) {
          let sellAmt = 0;
          if (req.sellType === SellType.PERCENTAGE) {
            sellAmt = lockedPensionBalance * ((req.sellPercentage ?? req.percentage ?? 0) / 100);
          } else {
            sellAmt = Math.min(req.sellAmount ?? req.amount ?? 0, lockedPensionBalance);
          }
          if (sellAmt > 0) {
            const netProceeds = sellAmt * (1 - PENSION_TAX.EARLY_WITHDRAWAL_RATE);
            lockedPensionBalance -= sellAmt;
            categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + netProceeds;
            totalFromSales += netProceeds;
          }
        }
      }

      // Process non-pension sells normally
      if (otherSells.length > 0) {
        if (useProducts) {
          const proceeds = processSellRequestsFromProducts(otherSells, products);
          totalFromSales += proceeds;
        }
        const proceeds = processSellRequests(otherSells, categoryBalances);
        if (!useProducts) totalFromSales += proceeds;
      }
    }

    // Apply cashflow impacts — inflows go to CASH (= buying credit)
    const inflows = eventsResult.inflows || 0;
    const creditFromInflows = eventsResult.creditFromInflows || 0;
    const outflows = eventsResult.outflows || 0;

    if (creditFromInflows > 0) {
      categoryBalances[AssetCategory.CASH] =
        (categoryBalances[AssetCategory.CASH] || 0) + creditFromInflows;
      if (useProducts) {
        products.push(
          createSyntheticProduct("הכנסה מאירוע", AssetCategory.CASH, creditFromInflows)
        );
      }
    }

    if (outflows > 0) {
      withdrawProportionally(outflows, categoryBalances);
      if (useProducts) {
        withdrawFromProductsProportionally(outflows, products);
      }
    }

    if (inflows > 0) {
      totalFromInflows += inflows;
    }
    if (!isRetired) {
      // Add savings to CASH (= buying credit)
      // Spending reduction → more left over; income reduction → less left over
      const effectiveSavings = Math.max(0, monthlySavings * 12 + (eventsResult.spendingReduction || 0) - (eventsResult.incomeReduction || 0));
      categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + effectiveSavings;
      if (useProducts) {
        products.push(createSyntheticProduct("חיסכון", AssetCategory.CASH, effectiveSavings));
      }
      totalFromSavings += effectiveSavings;
    }

    // Process buy from credit requests — withdraws from CASH, adds to target category
    if (eventsResult.buyFromCreditRequests.length > 0) {
      const cashBalance = categoryBalances[AssetCategory.CASH] || 0;
      const { spent } = processBuyFromCredit(
        eventsResult.buyFromCreditRequests,
        categoryBalances,
        cashBalance
      );
      if (useProducts) {
        const productCashBalance = products
          .filter(p => p.category === AssetCategory.CASH)
          .reduce((s, p) => s + p.balance, 0);
        processBuyFromCreditToProducts(
          eventsResult.buyFromCreditRequests,
          products,
          productCashBalance
        );
      }
      totalUsedForPurchases += spent;
    }

    // Apply returns (CASH earns 0%, other categories earn their rates)
    applyReturnsPerCategory(categoryBalances, returnAssumptions);
    if (useProducts) {
      applyReturnsPerProduct(products, returnAssumptions);
    }
  }

  // Include locked pension in the category balances so that the sell form
  // shows the correct pension value (even though it's in the locked pool).
  const outputBalances = { ...categoryBalances };
  if (lockedPensionBalance > 0) {
    outputBalances[AssetCategory.PENSION] = (outputBalances[AssetCategory.PENSION] || 0) + lockedPensionBalance;
  }

  return {
    availableCredit: categoryBalances[AssetCategory.CASH] || 0,
    breakdown: {
      fromSavings: totalFromSavings,
      fromSales: totalFromSales,
      fromInflows: totalFromInflows,
      usedForPurchases: totalUsedForPurchases,
    },
    categoryBalances: outputBalances,
    productBalances: useProducts ? products : undefined,
  };
}
